import { z } from "zod";

import { runtimeStageAssetIdForScene } from "./assets";
import { type M2BlueprintNode, type M2SceneBlueprint, m2Blueprint } from "./sceneBlueprints/m2";
import { type M3BlueprintNode, type M3SceneBlueprint, m3Blueprint } from "./sceneBlueprints/m3";
import { type M4BlueprintNode, type M4SceneBlueprint, m4Blueprint } from "./sceneBlueprints/m4";
import { type M5BlueprintNode, type M5SceneBlueprint, m5Blueprint } from "./sceneBlueprints/m5";
import { type M6BlueprintNode, type M6SceneBlueprint, m6Blueprint } from "./sceneBlueprints/m6";
import { pageRefSchema, provenanceTypeSchema, reviewStateSchema } from "./schema";

const runtimeSourceAttributionSchema = z
  .object({
    sourceCategory: z.string().min(1),
    provenanceType: provenanceTypeSchema,
    sourceRefs: z.array(z.string().min(1)).min(1),
    citationLabel: z.string().min(1),
    pageRefs: z.array(pageRefSchema),
  })
  .superRefine((source, context) => {
    if (source.provenanceType === "textbook" && source.pageRefs.length === 0) {
      context.addIssue({
        code: "custom",
        message: "教材节点必须同时登记书内印刷页与PDF页。",
        path: ["pageRefs"],
      });
    }
    if (
      source.provenanceType === "local_extension" &&
      (source.sourceCategory === "教材正文" || source.pageRefs.length > 0)
    ) {
      context.addIssue({
        code: "custom",
        message: "地方拓展不得冒充教材正文或复用教材页码。",
        path: ["sourceCategory"],
      });
    }
  });

const runtimeAssetSuggestionSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  title: z.string().min(1),
  usage: z.string().min(1),
  implementationState: z.enum(["implemented", "planned", "retired"]),
  requiredForRelease: z.boolean(),
  provenanceGuard: z.string().min(1).optional(),
  representationLabel: z.string().min(1).optional(),
  rightsPlan: z.string().min(1).optional(),
  visibleLabel: z.string().min(1).optional(),
});

const runtimeInteractionSchema = z.object({
  mode: z.string().min(1),
  learnerAction: z.string().min(1),
  feedback: z.string().min(1),
  persistedState: z.string().min(1).optional(),
  historyConstraint: z.string().min(1),
  nonScored: z.literal(true),
  actualInteraction: z.boolean().optional(),
  keyboardOperable: z.boolean().optional(),
  formalAssessment: z.boolean().optional(),
});

const runtimePresentationSchema = z.object({
  mode: z.string().min(1),
  visualSummary: z.string().min(1).optional(),
  interaction: runtimeInteractionSchema.nullable(),
});

const runtimeVerbatimQuoteSchema = z.object({
  text: z.string().min(1),
  context: z.string().min(1),
  verbatim: z.literal(true),
});

const runtimeDateMarkerSchema = z.object({
  date: z.string().min(1),
  category: z.string().min(1),
  label: z.string().min(1),
});

export const runtimeSceneNodeSchema = z
  .object({
    id: z.string().min(1),
    sceneId: z.string().regex(/^M[2-6]S[1-4]$/),
    order: z.number().int().positive(),
    nodeType: z.string().min(1),
    title: z.string().min(1),
    timeAnchor: z.string().min(1),
    venue: z.string().min(1).optional(),
    estimatedSeconds: z.number().int().positive(),
    claimId: z.string().min(1),
    claim: z.string().min(1),
    bodyZh: z.string().min(1).nullable(),
    evidencePoints: z.array(z.string().min(1)),
    verbatimQuotes: z.array(runtimeVerbatimQuoteSchema),
    dateMarkers: z.array(runtimeDateMarkerSchema),
    source: runtimeSourceAttributionSchema,
    presentation: runtimePresentationSchema,
    sensitivityLevel: z.string().min(1).nullable(),
    tone: z.string().min(1).optional(),
    guardrails: z.array(z.string().min(1)),
    assetRefs: z.array(z.string().regex(/^AST-[A-Z0-9-]+$/)).min(1),
    assetSuggestions: z.array(runtimeAssetSuggestionSchema).min(1),
    visibleLabel: z.string().min(1).optional(),
    reviewState: reviewStateSchema,
  })
  .superRefine((node, context) => {
    if (node.bodyZh === null && node.evidencePoints.length === 0) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须保留正文或证据点。`,
        path: ["bodyZh"],
      });
    }
    if (node.source.provenanceType === "local_extension" && node.visibleLabel !== "拓展材料") {
      context.addIssue({
        code: "custom",
        message: `${node.id}地方拓展必须显示“拓展材料”。`,
        path: ["visibleLabel"],
      });
    }
    if (node.source.provenanceType === "textbook" && node.visibleLabel === "拓展材料") {
      context.addIssue({
        code: "custom",
        message: `${node.id}教材正文不得标成地方拓展。`,
        path: ["visibleLabel"],
      });
    }
    const implementedSuggestionIds = node.assetSuggestions
      .filter((asset) => asset.implementationState === "implemented")
      .map((asset) => asset.id);
    if (
      JSON.stringify(implementedSuggestionIds) !== JSON.stringify(node.assetRefs) ||
      implementedSuggestionIds.some((assetId) => !/^AST-[A-Z0-9-]+$/.test(assetId))
    ) {
      context.addIssue({
        code: "custom",
        message: `${node.id}的已实现资产建议必须与运行资产引用逐项一致。`,
        path: ["assetSuggestions"],
      });
    }
  });

const runtimeFormalTaskSchema = z.object({
  id: z.string().regex(/^T(0[5-9]|1[0-9])$/),
  sequenceIndex: z.number().int().nonnegative(),
  anchorNodeId: z.string().min(1).optional(),
  relation: z.string().min(1).optional(),
});

const runtimeExtensionLabelSchema = z.object({
  id: z.string().min(1),
  label: z.enum(["【广西融入②】", "【广西融入⑤】"]),
  visibleLabel: z.literal("拓展材料"),
  appliesTo: z.array(z.string().min(1)).min(1),
  sequenceIndex: z.number().int().nonnegative(),
});

export const runtimeSequenceStepSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("node"),
    id: z.string().min(1),
    sequenceIndex: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("formal_task"),
    id: z.string().regex(/^T(0[5-9]|1[0-9])$/),
    sequenceIndex: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("local_extension_label"),
    id: z.string().min(1),
    label: z.enum(["【广西融入②】", "【广西融入⑤】"]),
    visibleLabel: z.literal("拓展材料"),
    appliesTo: z.array(z.string().min(1)).min(1),
    sequenceIndex: z.number().int().nonnegative(),
  }),
]);

const runtimeTimingSchema = z
  .object({
    mainNarrationSeconds: z.number().int().positive(),
    extensionNarrationSeconds: z.number().int().nonnegative(),
    totalNarrationSeconds: z.number().int().positive(),
  })
  .superRefine((timing, context) => {
    if (
      timing.totalNarrationSeconds !==
      timing.mainNarrationSeconds + timing.extensionNarrationSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "场景总时长必须等于教材主体与地方拓展时长之和。",
        path: ["totalNarrationSeconds"],
      });
    }
  });

export const runtimeSceneBlueprintSchema = z
  .object({
    id: z.string().regex(/^M[2-6]S[1-4]$/),
    moduleId: z.string().regex(/^M[2-6]$/),
    moduleOrder: z.number().int().min(2).max(6),
    moduleTitle: z.string().min(1),
    sceneOrder: z.number().int().positive(),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    pageRefs: z.array(pageRefSchema),
    assetRefs: z.array(z.string().regex(/^AST-[A-Z0-9-]+$/)).min(1),
    sequenceIds: z.array(z.string().min(1)).min(1),
    sequence: z.array(runtimeSequenceStepSchema).min(1),
    nodes: z.array(runtimeSceneNodeSchema).min(1),
    formalTasks: z.array(runtimeFormalTaskSchema),
    extensionLabels: z.array(runtimeExtensionLabelSchema),
    timing: runtimeTimingSchema,
    reviewState: reviewStateSchema,
  })
  .superRefine((scene, context) => {
    if (!scene.id.startsWith(`${scene.moduleId}S`)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}与模块${scene.moduleId}不一致。`,
        path: ["moduleId"],
      });
    }

    const stepIds = scene.sequence.map((step) => step.id);
    if (
      scene.sequence.some((step, index) => step.sequenceIndex !== index) ||
      JSON.stringify(stepIds) !== JSON.stringify(scene.sequenceIds)
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}的类型化序列必须逐项保留原始sequence。`,
        path: ["sequence"],
      });
    }

    const nodeIds = scene.nodes.map((node) => node.id);
    const sequenceNodeIds = scene.sequence
      .filter((step) => step.kind === "node")
      .map((step) => step.id);
    if (
      scene.nodes.some((node) => node.sceneId !== scene.id) ||
      JSON.stringify(sequenceNodeIds) !== JSON.stringify(nodeIds)
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}必须按原顺序完整加载全部内容节点。`,
        path: ["nodes"],
      });
    }
    if (
      scene.nodes.some((node) => JSON.stringify(node.assetRefs) !== JSON.stringify(scene.assetRefs))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}的场景运行资产与全部节点已实现资产必须一致。`,
        path: ["assetRefs"],
      });
    }

    const taskSteps = scene.sequence.filter((step) => step.kind === "formal_task");
    if (
      taskSteps.length !== scene.formalTasks.length ||
      taskSteps.some(
        (step, index) =>
          step.id !== scene.formalTasks[index]?.id ||
          step.sequenceIndex !== scene.formalTasks[index]?.sequenceIndex,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}正式任务的插入位置或顺序丢失。`,
        path: ["formalTasks"],
      });
    }
    if (
      scene.formalTasks.some(
        (task) => task.anchorNodeId !== undefined && !nodeIds.includes(task.anchorNodeId),
      )
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}正式任务锚点必须引用本场景节点。`,
        path: ["formalTasks"],
      });
    }

    const labelSteps = scene.sequence.filter((step) => step.kind === "local_extension_label");
    if (
      labelSteps.length !== scene.extensionLabels.length ||
      labelSteps.some(
        (step, index) =>
          step.id !== scene.extensionLabels[index]?.id ||
          step.sequenceIndex !== scene.extensionLabels[index]?.sequenceIndex,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}地方拓展标签的插入位置或可见信息丢失。`,
        path: ["extensionLabels"],
      });
    }
    for (const extension of scene.extensionLabels) {
      if (extension.appliesTo.some((nodeId) => !nodeIds.includes(nodeId))) {
        context.addIssue({
          code: "custom",
          message: `${extension.id}只能关联本场景的地方拓展节点。`,
          path: ["extensionLabels"],
        });
      }
    }
  });

export const runtimeSceneBlueprintCollectionSchema = z
  .array(runtimeSceneBlueprintSchema)
  .superRefine((scenes, context) => {
    const sceneIds = scenes.map((scene) => scene.id);
    if (new Set(sceneIds).size !== sceneIds.length) {
      context.addIssue({ code: "custom", message: "运行时场景ID必须唯一。" });
    }
    const taskIds = scenes.flatMap((scene) => scene.formalTasks.map((task) => task.id));
    if (new Set(taskIds).size !== taskIds.length) {
      context.addIssue({ code: "custom", message: "正式任务不得跨场景重复加载。" });
    }
    const serialized = JSON.stringify(scenes);
    if (serialized.includes("广西融入③") || serialized.includes("广西融入④")) {
      context.addIssue({ code: "custom", message: "已删除的广西融入③、④不得进入运行时。" });
    }
  });

export type RuntimeSceneNode = z.infer<typeof runtimeSceneNodeSchema>;
export type RuntimeSceneBlueprint = z.infer<typeof runtimeSceneBlueprintSchema>;

export const confirmedGuangxiRuntimeRelocations = {
  M2S2_GX07: { sceneId: "M3S2", nodeId: "M3S2_GX07", pageCount: 6 },
  M2S2_GX08: { sceneId: "M4S3", nodeId: "M4S3_GX08", pageCount: 3 },
} as const;
export type RuntimeAssetSuggestion = z.infer<typeof runtimeAssetSuggestionSchema>;
export type RuntimeInteraction = z.infer<typeof runtimeInteractionSchema>;

type SourceAttributionInput = {
  sourceCategory: string;
  provenanceType: z.infer<typeof provenanceTypeSchema>;
  sourceRefs: readonly string[];
  citationLabel: string;
  pageRefs: ReadonlyArray<{ print: string; pdf: string }>;
};

type SourceAssetSuggestion =
  | M2BlueprintNode["assetSuggestions"][number]
  | M3BlueprintNode["assetSuggestions"][number]
  | M4BlueprintNode["assetSuggestions"][number]
  | M5BlueprintNode["assetSuggestions"][number]
  | M6BlueprintNode["assetSuggestions"][number];

type ExtensionLabelInput = {
  readonly id: string;
  readonly label: "【广西融入②】" | "【广西融入⑤】";
  readonly visibleLabel: "拓展材料";
  readonly appliesTo: readonly string[];
};

const studentFacingCopyReplacements = [
  ["教材依据", "史料依据"],
  ["依据教材", "依据史料"],
  ["教材双页码", "来源核验信息"],
  ["教材页码", "来源核验信息"],
  ["来源页码", "来源核验信息"],
  ["双页码", "来源核验信息"],
  ["教材证据", "史料证据"],
  ["教材出处", "史料出处"],
  ["教材原句", "史料原句"],
  ["教材引文", "史料引文"],
  ["教材摘录", "史料摘录"],
  ["教材载述", "史料载述"],
  ["教材所载", "史料所载"],
  ["教材记载", "史料记载"],
  ["教材统计", "史料统计"],
  ["教材结论", "史料结论"],
  ["教材事实", "史料事实"],
  ["教材评价", "史料评价"],
  ["教材原有语义", "已核验语义"],
  ["教材原文", "已核验原文"],
  ["教材用语", "已核验用语"],
  ["教材表述", "已核验表述"],
  ["教材确认的", "已核验的"],
  ["教材确认", "史料确认"],
  ["教材给出的", "已核验的"],
  ["教材支持的", "史料支持的"],
  ["教材支持", "史料支持"],
  ["教材列举的", "史料列举的"],
  ["教材列举", "史料列举"],
  ["教材口径", "已核验口径"],
  ["参考教材", "参考史料"],
  ["按教材", "按已核验史料"],
  ["教材中的", "史料中的"],
  ["教材中", "史料中"],
  ["教材段落", "史料段落"],
  ["教材数字", "已核验数字"],
  ["教材外", "现有史料外"],
  ["未经教材", "未经史料"],
] as const;

function sanitizeStudentFacingCopy(value: string): string;
function sanitizeStudentFacingCopy(value: null): null;
function sanitizeStudentFacingCopy(value: undefined): undefined;
function sanitizeStudentFacingCopy(value: string | null | undefined): string | null | undefined {
  if (typeof value !== "string") return value;
  const importantTextbookTerm = "__IMPORTANT_PARTY_EDUCATION_TEXTBOOK__";
  return studentFacingCopyReplacements
    .reduce(
      (copy, [source, replacement]) => copy.replaceAll(source, replacement),
      value.replaceAll("重要教材", importantTextbookTerm),
    )
    .replaceAll("教材", "史料")
    .replaceAll(importantTextbookTerm, "重要教材");
}

const adaptSource = (source: SourceAttributionInput): RuntimeSceneNode["source"] => ({
  sourceCategory: source.sourceCategory,
  provenanceType: source.provenanceType,
  sourceRefs: [...source.sourceRefs],
  citationLabel: source.citationLabel,
  pageRefs: source.pageRefs.map((pageRef) => ({ ...pageRef })),
});

const runtimeNodeVenueById = {
  M4S3_GX08: "桂林·八路军桂林办事处档案场域",
  M3S2_GX07: "钦廉、临桂—阳朔、桂东南等广西抗日武装斗争离散档案场域",
  M4S3_GX02_C06: "桂林与昭平黄姚抗战文化运动离散档案场域",
} as const;

const adaptRuntimeNodeVenue = (nodeId: string): Partial<Pick<RuntimeSceneNode, "venue">> => {
  const venue = runtimeNodeVenueById[nodeId as keyof typeof runtimeNodeVenueById];
  return venue ? { venue } : {};
};

const adaptAssetSuggestion = (asset: SourceAssetSuggestion): RuntimeAssetSuggestion => {
  if ("key" in asset) {
    return {
      id: asset.key,
      kind: asset.kind,
      title: sanitizeStudentFacingCopy(asset.title),
      usage: sanitizeStudentFacingCopy(asset.usage),
      implementationState: "retired",
      requiredForRelease: false,
      provenanceGuard: sanitizeStudentFacingCopy(asset.provenanceGuard),
      ...(asset.suggestedVisibleLabel
        ? { visibleLabel: sanitizeStudentFacingCopy(asset.suggestedVisibleLabel) }
        : {}),
    };
  }
  if ("label" in asset) {
    return {
      id: asset.id,
      kind: asset.kind,
      title: sanitizeStudentFacingCopy(asset.label),
      usage: sanitizeStudentFacingCopy(asset.purpose),
      implementationState: "retired",
      requiredForRelease: false,
      representationLabel: sanitizeStudentFacingCopy(asset.representationLabel),
      rightsPlan: asset.rightsPlan,
    };
  }
  return {
    id: asset.id,
    kind: asset.kind,
    title: sanitizeStudentFacingCopy(asset.title),
    usage: sanitizeStudentFacingCopy(asset.usage),
    implementationState: "retired",
    requiredForRelease: false,
    provenanceGuard: sanitizeStudentFacingCopy(asset.provenanceGuard),
    representationLabel: sanitizeStudentFacingCopy(asset.representationLabel),
    rightsPlan: asset.rightsPlan,
  };
};

const adaptRuntimeAssets = (
  sceneId: string,
  suggestions: readonly SourceAssetSuggestion[],
): Pick<RuntimeSceneNode, "assetRefs" | "assetSuggestions"> => {
  const assetId = runtimeStageAssetIdForScene(sceneId);
  return {
    assetRefs: [assetId],
    assetSuggestions: [
      {
        id: assetId,
        kind: "media",
        title: "统一沉浸历史复原空间",
        usage: "以当前知识节点的时间、场域和证据点驱动全屏历史复原空间与可访问热点。",
        implementationState: "implemented",
        requiredForRelease: true,
        provenanceGuard: "复原影像仅作教学环境，不冒充档案照片或视频；人物与陈设不作为史实依据。",
        representationLabel: "AI辅助复原影像＋原创交互层",
        rightsPlan: "AI辅助原创视觉与原创代码；来源、提示和逐文件哈希进入provenance台账。",
        visibleLabel: "复原性创作·非档案影像",
      },
      ...suggestions.map(adaptAssetSuggestion),
    ],
  };
};

const distinctPageRefs = (
  nodes: ReadonlyArray<{ source: SourceAttributionInput }>,
): RuntimeSceneBlueprint["pageRefs"] => {
  const seen = new Set<string>();
  return nodes.flatMap((node) =>
    node.source.provenanceType === "textbook"
      ? node.source.pageRefs.flatMap((pageRef) => {
          const key = `${pageRef.print}|${pageRef.pdf}`;
          if (seen.has(key)) return [];
          seen.add(key);
          return [{ ...pageRef }];
        })
      : [],
  );
};

const adaptExtensionLabel = (
  label: ExtensionLabelInput,
  sequenceIds: readonly string[],
): RuntimeSceneBlueprint["extensionLabels"][number] => ({
  id: label.id,
  label: label.label,
  visibleLabel: label.visibleLabel,
  appliesTo: [...label.appliesTo],
  sequenceIndex: sequenceIds.indexOf(label.id),
});

type FormalTaskInput = {
  id: string;
  anchorNodeId?: string;
  relation?: string;
};

const makeRuntimeScene = (input: {
  id: string;
  moduleId: string;
  moduleOrder: number;
  moduleTitle: string;
  sceneOrder: number;
  title: string;
  identity: "抗战史料研学员";
  timeLabel: string;
  mainVenue: string;
  archiveSlices: readonly string[];
  pageRefs: ReadonlyArray<{ print: string; pdf: string }>;
  sequenceIds: readonly string[];
  nodes: readonly RuntimeSceneNode[];
  formalTasks: readonly FormalTaskInput[];
  extensionLabels: readonly ExtensionLabelInput[];
  timing: RuntimeSceneBlueprint["timing"];
  reviewState: RuntimeSceneBlueprint["reviewState"];
}): RuntimeSceneBlueprint => {
  const extensionLabels = input.extensionLabels.map((label) =>
    adaptExtensionLabel(label, input.sequenceIds),
  );
  const extensionById = new Map(extensionLabels.map((label) => [label.id, label]));
  const nodeIds = new Set(input.nodes.map((node) => node.id));
  const taskIds = new Set(input.formalTasks.map((task) => task.id));

  const sequence = input.sequenceIds.map((id, sequenceIndex) => {
    const extension = extensionById.get(id);
    if (extension) {
      return {
        kind: "local_extension_label" as const,
        id,
        label: extension.label,
        visibleLabel: extension.visibleLabel,
        appliesTo: extension.appliesTo,
        sequenceIndex,
      };
    }
    if (taskIds.has(id)) return { kind: "formal_task" as const, id, sequenceIndex };
    if (nodeIds.has(id)) return { kind: "node" as const, id, sequenceIndex };
    throw new Error(`${input.id}的sequence包含未声明项目：${id}`);
  });

  return runtimeSceneBlueprintSchema.parse({
    id: input.id,
    moduleId: input.moduleId,
    moduleOrder: input.moduleOrder,
    moduleTitle: sanitizeStudentFacingCopy(input.moduleTitle),
    sceneOrder: input.sceneOrder,
    title: sanitizeStudentFacingCopy(input.title),
    identity: input.identity,
    timeLabel: input.timeLabel,
    mainVenue: sanitizeStudentFacingCopy(input.mainVenue),
    archiveSlices: input.archiveSlices.map((value) => sanitizeStudentFacingCopy(value)),
    pageRefs: input.pageRefs.map((pageRef) => ({ ...pageRef })),
    assetRefs: [runtimeStageAssetIdForScene(input.id)],
    sequenceIds: [...input.sequenceIds],
    sequence,
    nodes: input.nodes,
    formalTasks: input.formalTasks.map((task) => ({
      id: task.id,
      sequenceIndex: input.sequenceIds.indexOf(task.id),
      ...(task.anchorNodeId ? { anchorNodeId: task.anchorNodeId } : {}),
      ...(task.relation ? { relation: sanitizeStudentFacingCopy(task.relation) } : {}),
    })),
    extensionLabels,
    timing: input.timing,
    reviewState: input.reviewState,
  });
};

const adaptM2Node = (node: M2BlueprintNode): RuntimeSceneNode => ({
  id: node.id,
  sceneId: node.sceneId,
  order: node.order,
  nodeType: node.nodeType,
  title: sanitizeStudentFacingCopy(node.title),
  timeAnchor: node.timeAnchor,
  ...adaptRuntimeNodeVenue(node.id),
  estimatedSeconds: node.estimatedSeconds,
  claimId: node.claimId,
  claim: sanitizeStudentFacingCopy(node.claim),
  bodyZh: sanitizeStudentFacingCopy(node.bodyZh),
  evidencePoints: [],
  verbatimQuotes: [],
  dateMarkers: [],
  source: adaptSource(node.source),
  presentation: { mode: node.presentationType, interaction: null },
  sensitivityLevel: null,
  guardrails: [],
  ...adaptRuntimeAssets(node.sceneId, node.assetSuggestions),
  ...(node.visibleLabel ? { visibleLabel: sanitizeStudentFacingCopy(node.visibleLabel) } : {}),
  reviewState: node.reviewState,
});

const requiredM2LocalNode = (nodeId: "M2S2_GX07" | "M2S2_GX08") => {
  const node = m2Blueprint.scenes
    .find((scene) => scene.id === "M2S2")
    ?.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) throw new Error(`${nodeId}地方拓展节点缺失。`);
  return node;
};

const relocatedGuangxiResistanceNode = (): RuntimeSceneNode => {
  const source = requiredM2LocalNode("M2S2_GX07");
  return {
    ...adaptM2Node(source),
    id: "M3S2_GX07",
    sceneId: "M3S2",
    order: 25,
    claimId: "M3S2_CLAIM_GX_ARMED_RESISTANCE_TIMELINE",
    ...adaptRuntimeNodeVenue("M3S2_GX07"),
    ...adaptRuntimeAssets("M3S2", source.assetSuggestions),
  };
};

const relocatedGuilinOfficeNode = (): RuntimeSceneNode => {
  const source = requiredM2LocalNode("M2S2_GX08");
  return {
    ...adaptM2Node(source),
    id: "M4S3_GX08",
    sceneId: "M4S3",
    order: 35,
    claimId: "M4S3_CLAIM_GX_GUILIN_OFFICE",
    ...adaptRuntimeNodeVenue("M4S3_GX08"),
    ...adaptRuntimeAssets("M4S3", source.assetSuggestions),
  };
};

const adaptM3Node = (node: M3BlueprintNode): RuntimeSceneNode => ({
  id: node.id,
  sceneId: node.sceneId,
  order: node.order,
  nodeType: node.nodeType,
  title: sanitizeStudentFacingCopy(node.title),
  timeAnchor: node.timeAnchor,
  ...adaptRuntimeNodeVenue(node.id),
  estimatedSeconds: node.estimatedSeconds,
  claimId: node.claimId,
  claim: sanitizeStudentFacingCopy(node.claim),
  bodyZh: null,
  evidencePoints: node.evidencePoints.map((value) => sanitizeStudentFacingCopy(value)),
  verbatimQuotes: node.verbatimQuotes.map((quote) => ({
    ...quote,
    text: sanitizeStudentFacingCopy(quote.text),
    context: sanitizeStudentFacingCopy(quote.context),
  })),
  dateMarkers: [],
  source: adaptSource(node.source),
  presentation: {
    mode: node.render.kind,
    visualSummary: sanitizeStudentFacingCopy(node.render.visualSummary),
    interaction: {
      mode: node.render.interaction.mode,
      learnerAction: sanitizeStudentFacingCopy(node.render.interaction.learnerAction),
      feedback: sanitizeStudentFacingCopy(node.render.interaction.feedback),
      historyConstraint: sanitizeStudentFacingCopy(node.render.interaction.historyConstraint),
      nonScored: node.render.interaction.nonScored,
    },
  },
  sensitivityLevel: node.sensitivity,
  guardrails: node.render.guardrails.map((value) => sanitizeStudentFacingCopy(value)),
  ...adaptRuntimeAssets(node.sceneId, node.assetSuggestions),
  reviewState: node.reviewState,
});

const adaptM4Node = (node: M4BlueprintNode): RuntimeSceneNode => ({
  id: node.id,
  sceneId: node.sceneId,
  order: node.order,
  nodeType: node.nodeType,
  title: sanitizeStudentFacingCopy(node.title),
  timeAnchor: node.timeAnchor,
  ...adaptRuntimeNodeVenue(node.id),
  estimatedSeconds: node.estimatedSeconds,
  claimId: node.claimId,
  claim: sanitizeStudentFacingCopy(node.claim),
  bodyZh: sanitizeStudentFacingCopy(node.bodyZh),
  evidencePoints: [],
  verbatimQuotes: [],
  dateMarkers: [],
  source: adaptSource(node.source),
  presentation: {
    mode: node.presentation.mode,
    visualSummary: sanitizeStudentFacingCopy(node.presentation.visualSummary),
    interaction: {
      mode: node.presentation.interaction.mode,
      learnerAction: sanitizeStudentFacingCopy(node.presentation.interaction.learnerAction),
      feedback: sanitizeStudentFacingCopy(node.presentation.interaction.evidenceFeedback),
      persistedState: sanitizeStudentFacingCopy(node.presentation.interaction.persistedState),
      historyConstraint: sanitizeStudentFacingCopy(node.presentation.interaction.historyConstraint),
      nonScored: node.presentation.interaction.nonScored,
      actualInteraction: node.presentation.interaction.actualInteraction,
    },
  },
  sensitivityLevel: node.sensitivity,
  guardrails: node.presentation.guardrails.map((value) => sanitizeStudentFacingCopy(value)),
  ...adaptRuntimeAssets(node.sceneId, node.assetSuggestions),
  ...(node.visibleLabel ? { visibleLabel: sanitizeStudentFacingCopy(node.visibleLabel) } : {}),
  reviewState: node.reviewState,
});

const adaptM5Node = (node: M5BlueprintNode): RuntimeSceneNode => ({
  id: node.id,
  sceneId: node.sceneId,
  order: node.order,
  nodeType: node.nodeType,
  title: sanitizeStudentFacingCopy(node.title),
  timeAnchor: node.timeAnchor,
  estimatedSeconds: node.estimatedSeconds,
  claimId: node.claimId,
  claim: sanitizeStudentFacingCopy(node.claim),
  bodyZh: sanitizeStudentFacingCopy(node.bodyZh),
  evidencePoints: [],
  verbatimQuotes: [],
  dateMarkers: [],
  source: adaptSource(node.source),
  presentation: {
    mode: node.presentationType,
    interaction: {
      mode: node.interactionSuggestion.kind,
      learnerAction: sanitizeStudentFacingCopy(node.interactionSuggestion.action),
      feedback: sanitizeStudentFacingCopy(node.interactionSuggestion.feedback),
      historyConstraint: sanitizeStudentFacingCopy(node.interactionSuggestion.historicalGuard),
      nonScored: true,
      formalAssessment: node.interactionSuggestion.formalAssessment,
    },
  },
  sensitivityLevel: null,
  tone: node.tone,
  guardrails: [sanitizeStudentFacingCopy(node.interactionSuggestion.historicalGuard)],
  ...adaptRuntimeAssets(node.sceneId, node.assetSuggestions),
  reviewState: node.reviewState,
});

const adaptM6Node = (node: M6BlueprintNode): RuntimeSceneNode => ({
  id: node.id,
  sceneId: node.sceneId,
  order: node.order,
  nodeType: node.nodeType,
  title: sanitizeStudentFacingCopy(node.title),
  timeAnchor: node.timeAnchor,
  estimatedSeconds: node.estimatedSeconds,
  claimId: node.claimId,
  claim: sanitizeStudentFacingCopy(node.claim),
  bodyZh: null,
  evidencePoints: node.evidencePoints.map((value) => sanitizeStudentFacingCopy(value)),
  verbatimQuotes: node.verbatimQuotes.map((quote) => ({
    ...quote,
    text: sanitizeStudentFacingCopy(quote.text),
    context: sanitizeStudentFacingCopy(quote.context),
  })),
  dateMarkers: node.dateMarkers?.map((marker) => ({ ...marker })) ?? [],
  source: adaptSource(node.source),
  presentation: {
    mode: node.render.kind,
    visualSummary: sanitizeStudentFacingCopy(node.render.visualSummary),
    interaction: {
      mode: node.render.interaction.mode,
      learnerAction: sanitizeStudentFacingCopy(node.render.interaction.learnerAction),
      feedback: sanitizeStudentFacingCopy(node.render.interaction.feedback),
      historyConstraint: sanitizeStudentFacingCopy(node.render.interaction.historyConstraint),
      nonScored: node.render.interaction.nonScored,
      keyboardOperable: node.render.interaction.keyboardOperable,
    },
  },
  sensitivityLevel: node.sensitivity,
  guardrails: node.render.guardrails.map((value) => sanitizeStudentFacingCopy(value)),
  ...adaptRuntimeAssets(node.sceneId, node.assetSuggestions),
  reviewState: node.reviewState,
});

const adaptM2Scene = (scene: M2SceneBlueprint): RuntimeSceneBlueprint => {
  const nodes =
    scene.id === "M2S2"
      ? scene.nodes.filter((node) => node.source.provenanceType !== "local_extension")
      : scene.nodes;
  const sequenceIds =
    scene.id === "M2S2"
      ? scene.sequence.filter((id) => !["GX05_LABEL", "M2S2_GX08", "M2S2_GX07"].includes(id))
      : scene.sequence;
  return makeRuntimeScene({
    id: scene.id,
    moduleId: m2Blueprint.id,
    moduleOrder: 2,
    moduleTitle: m2Blueprint.title,
    sceneOrder: scene.order,
    title: scene.title,
    identity: scene.identity,
    timeLabel: scene.id === "M2S2" ? "1937年11月—1938年4月" : scene.timeLabel,
    mainVenue: scene.mainVenue,
    archiveSlices: scene.id === "M2S2" ? scene.archiveSlices.slice(0, 2) : scene.archiveSlices,
    pageRefs: distinctPageRefs(nodes),
    sequenceIds,
    nodes: nodes.map(adaptM2Node),
    formalTasks: scene.taskIds.map((id) => ({ id })),
    extensionLabels: scene.id === "M2S2" ? [] : scene.extensionLabel ? [scene.extensionLabel] : [],
    timing:
      scene.id === "M2S2"
        ? {
            mainNarrationSeconds: scene.timing.mainNarrationSeconds,
            extensionNarrationSeconds: 0,
            totalNarrationSeconds: scene.timing.mainNarrationSeconds,
          }
        : scene.timing,
    reviewState: scene.reviewState,
  });
};

const adaptM3Scene = (scene: M3SceneBlueprint): RuntimeSceneBlueprint => {
  const textbookNodes = scene.nodes.map(adaptM3Node);
  const localNode = scene.id === "M3S2" ? relocatedGuangxiResistanceNode() : undefined;
  const nodes = localNode
    ? [...textbookNodes.slice(0, 2), localNode, ...textbookNodes.slice(2)]
    : textbookNodes;
  const sequenceIds =
    scene.id === "M3S2"
      ? [
          "M3S2_N01",
          "M3S2_N02",
          "M3S2_GX05_LABEL",
          "M3S2_GX07",
          "M3S2_N03",
          "M3S2_N04",
          "M3S2_N05",
          "T09",
        ]
      : scene.sequence;
  const extensionLabels: ExtensionLabelInput[] = localNode
    ? [
        {
          id: "M3S2_GX05_LABEL",
          label: "【广西融入⑤】",
          visibleLabel: "拓展材料",
          appliesTo: [localNode.id],
        },
      ]
    : [];
  return makeRuntimeScene({
    id: scene.id,
    moduleId: m3Blueprint.id,
    moduleOrder: 3,
    moduleTitle: m3Blueprint.title,
    sceneOrder: scene.order,
    title: scene.title,
    identity: scene.identity,
    timeLabel: scene.id === "M3S2" ? "1939年—1945年初；重点为1940年8月—1941年1月" : scene.timeLabel,
    mainVenue: scene.mainVenue,
    archiveSlices:
      scene.id === "M3S2"
        ? [
            ...scene.archiveSlices,
            "1939年11月·钦廉、邕宁八尺区与中越边境",
            "1944年夏—9月·临桂—阳朔",
            "1945年初·桂东南与钦廉四属",
          ]
        : scene.archiveSlices,
    pageRefs: scene.pageRefs,
    sequenceIds,
    nodes,
    formalTasks: scene.formalTaskIds.map((id) => ({
      id,
      ...(scene.taskAnchorNodeId ? { anchorNodeId: scene.taskAnchorNodeId } : {}),
    })),
    extensionLabels,
    timing: {
      mainNarrationSeconds: scene.lectureBudgetSeconds,
      extensionNarrationSeconds: localNode?.estimatedSeconds ?? 0,
      totalNarrationSeconds: scene.lectureBudgetSeconds + (localNode?.estimatedSeconds ?? 0),
    },
    reviewState: scene.reviewState,
  });
};

const adaptM4Scene = (scene: M4SceneBlueprint): RuntimeSceneBlueprint => {
  const textbookAndCultureNodes = scene.nodes.map(adaptM4Node);
  const localNode = scene.id === "M4S3" ? relocatedGuilinOfficeNode() : undefined;
  const nodes = localNode
    ? [...textbookAndCultureNodes.slice(0, 3), localNode, ...textbookAndCultureNodes.slice(3)]
    : textbookAndCultureNodes;
  const sequenceIds =
    scene.id === "M4S3"
      ? [
          "M4S3_N01",
          "M4S3_N02",
          "M4S3_N03",
          "M4S3_GX05_LABEL",
          "M4S3_GX08",
          "M4S3_GX02_LABEL",
          "M4S3_GX02_C06",
          "T14",
        ]
      : scene.sequence;
  const extensionLabels: ExtensionLabelInput[] = [
    ...(localNode
      ? [
          {
            id: "M4S3_GX05_LABEL",
            label: "【广西融入⑤】" as const,
            visibleLabel: "拓展材料" as const,
            appliesTo: [localNode.id],
          },
        ]
      : []),
    ...(scene.extensionLabel ? [scene.extensionLabel] : []),
  ];
  return makeRuntimeScene({
    id: scene.id,
    moduleId: m4Blueprint.id,
    moduleOrder: 4,
    moduleTitle: m4Blueprint.title,
    sceneOrder: scene.order,
    title: scene.title,
    identity: scene.identity,
    timeLabel:
      scene.id === "M4S3"
        ? "主线1943年—1944年10月；广西节点1938年10月后—1944年冬"
        : scene.timeLabel,
    mainVenue: scene.mainVenue,
    archiveSlices:
      scene.id === "M4S3"
        ? ["1938年11月—1941年1月·桂林八路军办事处", ...scene.archiveSlices]
        : scene.archiveSlices,
    pageRefs: scene.pageRefs,
    sequenceIds,
    nodes,
    formalTasks: scene.taskAnchors.map((anchor) => ({
      id: anchor.taskId,
      anchorNodeId: anchor.anchorNodeId,
      relation: anchor.relation,
    })),
    extensionLabels,
    timing: localNode
      ? {
          mainNarrationSeconds: scene.timing.mainNarrationSeconds,
          extensionNarrationSeconds:
            scene.timing.extensionNarrationSeconds + localNode.estimatedSeconds,
          totalNarrationSeconds: scene.timing.totalNarrationSeconds + localNode.estimatedSeconds,
        }
      : scene.timing,
    reviewState: scene.reviewState,
  });
};

const adaptM5Scene = (scene: M5SceneBlueprint): RuntimeSceneBlueprint =>
  makeRuntimeScene({
    id: scene.id,
    moduleId: m5Blueprint.id,
    moduleOrder: 5,
    moduleTitle: m5Blueprint.title,
    sceneOrder: scene.order,
    title: scene.title,
    identity: scene.identity,
    timeLabel: scene.timeLabel,
    mainVenue: scene.mainVenue,
    archiveSlices: scene.archiveSlices,
    pageRefs: distinctPageRefs(scene.nodes),
    sequenceIds: scene.sequence,
    nodes: scene.nodes.map(adaptM5Node),
    formalTasks: scene.taskIds.map((id) => ({ id })),
    extensionLabels: [],
    timing: {
      mainNarrationSeconds: scene.timing.mainNarrationSeconds,
      extensionNarrationSeconds: 0,
      totalNarrationSeconds: scene.timing.totalNarrationSeconds,
    },
    reviewState: scene.reviewState,
  });

const adaptM6Scene = (scene: M6SceneBlueprint): RuntimeSceneBlueprint =>
  makeRuntimeScene({
    id: scene.id,
    moduleId: m6Blueprint.id,
    moduleOrder: 6,
    moduleTitle: m6Blueprint.title,
    sceneOrder: scene.order,
    title: scene.title,
    identity: scene.identity,
    timeLabel: scene.timeLabel,
    mainVenue: scene.mainVenue,
    archiveSlices: scene.archiveSlices,
    pageRefs: scene.pageRefs,
    sequenceIds: scene.sequence,
    nodes: scene.nodes.map(adaptM6Node),
    formalTasks: scene.formalTaskIds.map((id) => ({
      id,
      ...(scene.taskAnchorNodeId ? { anchorNodeId: scene.taskAnchorNodeId } : {}),
    })),
    extensionLabels: [],
    timing: {
      mainNarrationSeconds: scene.lectureBudgetSeconds,
      extensionNarrationSeconds: 0,
      totalNarrationSeconds: scene.lectureBudgetSeconds,
    },
    reviewState: scene.reviewState,
  });

/**
 * 模块接入点集中在此处：新增或重构蓝图时，先提供单场景适配函数，再登记到本构建器。
 */
export const buildRuntimeSceneBlueprints = (): RuntimeSceneBlueprint[] =>
  runtimeSceneBlueprintCollectionSchema.parse([
    ...m2Blueprint.scenes.map(adaptM2Scene),
    ...m3Blueprint.scenes.map(adaptM3Scene),
    ...m4Blueprint.scenes.map(adaptM4Scene),
    ...m5Blueprint.scenes.map(adaptM5Scene),
    ...m6Blueprint.scenes.map(adaptM6Scene),
  ]);

export const runtimeSceneBlueprints = buildRuntimeSceneBlueprints();

export const runtimeSceneBlueprintById: Readonly<Record<string, RuntimeSceneBlueprint>> =
  Object.fromEntries(runtimeSceneBlueprints.map((scene) => [scene.id, scene]));
